import React from 'react';

import { useSelector, useDispatch } from 'react-redux';

import Avatar from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import UserAvatarCircleIcon from '@atlaskit/icon/glyph/user-avatar-circle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import IconWithFallback from 'src/components/icon-with-fallback';
import { LoadingStatus } from 'src/constants/loading-status';
import { useIntl } from 'src/hooks/intl';
import { changeFilterState, fetchJiraAssignees } from 'src/redux/jira/actions';
import { UNASSIGNED_USER_ID } from 'src/redux/jira/constants';
import {
  getAssigneesFetchedStatus,
  getAssignees,
  getSelectedAssignees,
} from 'src/redux/jira/selectors/repo-page-selectors';
import { Assignee, AssigneeType, UserOption } from 'src/redux/jira/types';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { BucketState } from 'src/types/state';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { MAX_ASSIGNEES_C0UNT, SCREEN_NAME } from '../../constants';
import { SingleSelectFilterDropdown } from '../filter-dropdown';

import { AssigneesFilterDropdown } from './assignees-filter-dropdown';
import * as styles from './assignees-filter.styled';
import messages from './assignees-filters.18n';

const emptyData = Array(MAX_ASSIGNEES_C0UNT).fill({
  isDisabled: true,
});

export const AssigneesFilter = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const selectedAssignees = useSelector<BucketState, string[]>(
    getSelectedAssignees
  );
  let assignees = useSelector<BucketState, Assignee[]>(getAssignees);
  const assigneesFetchedStatus = useSelector<BucketState, LoadingStatus>(
    getAssigneesFetchedStatus
  );

  const currentUser = useSelector(getCurrentUser);
  if (currentUser && currentUser.account_id) {
    const currentUserAsAssignee: Assignee = {
      displayName: currentUser.display_name,
      accountId: currentUser.account_id,
      avatarUrls: {
        '16x16': '',
        '24x24': '',
        '32x32': '',
        '48x48': '',
        '128x128': currentUser.links.avatar.href,
      },
    };

    assignees = [currentUserAsAssignee].concat(
      assignees.filter(
        assignee => assignee.accountId !== currentUser.account_id
      )
    );
  }

  const isLoading = assigneesFetchedStatus === LoadingStatus.Fetching;
  const isFailed = assigneesFetchedStatus === LoadingStatus.Failed;
  const assigneeClickedEvent = (assigneeType: AssigneeType) =>
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'assignee',
      actionSubjectId: 'assigneeFilter',
      source: SCREEN_NAME,
      attributes: {
        assigneeType,
      },
    });

  const handleAssigneeClick = (assigneeId: string) => {
    if (selectedAssignees.some(i => i === assigneeId)) {
      const newSelected = selectedAssignees.filter(i => i !== assigneeId);
      dispatch(
        changeFilterState({
          assignees: newSelected,
        })
      );
    } else {
      let assigneeType: AssigneeType;
      if (assigneeId === currentUser?.account_id) {
        assigneeType = AssigneeType.Self;
      } else {
        assigneeType = AssigneeType.Avatars;
      }
      assigneeClickedEvent(assigneeType);

      dispatch(
        changeFilterState({
          assignees: [assigneeId, ...selectedAssignees],
        })
      );
    }
  };

  const isSelected = (id: string) =>
    selectedAssignees.some(assigneeId => assigneeId === id);

  if (isLoading || isFailed) {
    return (
      <styles.AssigneesWrapper>
        <SingleSelectFilterDropdown
          onChange={() => {}}
          selected=""
          isLoading={isLoading}
          label=""
          options={[]}
          errorMessage={
            isFailed ? intl.formatMessage(messages.errorMessage) : ''
          }
          onRetry={() => dispatch(fetchJiraAssignees())}
          components={{
            CustomFilterDropdownTrigger: (
              <AvatarGroup maxCount={5} data={emptyData} />
            ),
          }}
          triggerAppearance="subtle"
        />
      </styles.AssigneesWrapper>
    );
  }

  const unassignedUser: UserOption = {
    label: intl.formatMessage(messages.unassigned),
    value: UNASSIGNED_USER_ID,
    avatarUrl: '',
    avatar: (
      <UserAvatarCircleIcon
        size="medium"
        label=""
        primaryColor={token('color.border')}
      />
    ),
  };

  const options = assignees.map(assignee => {
    const avatarUrl =
      assignee.avatarUrls['128x128'] || assignee.avatarUrls['48x48'];
    return {
      label: assignee.displayName,
      value: assignee.accountId,
      avatarUrl,
      avatar: (
        <IconWithFallback
          key={avatarUrl}
          src={avatarUrl}
          size="small"
          appearance="circle"
          fallback={<UserAvatarCircleIcon size="medium" label="" />}
        />
      ),
    };
  });

  // Split them into two groups so that the 2nd group
  // will appear in a dropdown
  let assigneesGroup1: UserOption[];
  let assigneesGroup2: UserOption[];
  if (options.length >= MAX_ASSIGNEES_C0UNT) {
    assigneesGroup1 = options.slice(0, MAX_ASSIGNEES_C0UNT - 1);
    assigneesGroup2 = [unassignedUser].concat(
      options.slice(MAX_ASSIGNEES_C0UNT - 1)
    );
  } else {
    assigneesGroup1 = [...options, unassignedUser];
    assigneesGroup2 = [];
  }

  if ((assigneesGroup1 || []).length > 0) {
    return (
      <styles.AssigneesWrapper>
        {assigneesGroup1.map((item, index) => (
          <styles.AssigneeItem
            key={item.value}
            zIndex={MAX_ASSIGNEES_C0UNT - index}
          >
            <styles.AvatarBorder isSelected={isSelected(item.value.toString())}>
              <Tooltip content={item.label}>
                <Avatar
                  size="medium"
                  name={item.label}
                  src={item.avatarUrl}
                  onClick={() => handleAssigneeClick(item.value.toString())}
                />
              </Tooltip>
            </styles.AvatarBorder>
          </styles.AssigneeItem>
        ))}
        {assigneesGroup2.length > 0 && (
          <styles.AssigneeItem key="more" zIndex={MAX_ASSIGNEES_C0UNT + 1}>
            <AssigneesFilterDropdown
              options={assigneesGroup2}
              value={assigneesGroup2?.filter(user =>
                selectedAssignees.includes(user.value.toString())
              )}
            />
          </styles.AssigneeItem>
        )}
      </styles.AssigneesWrapper>
    );
  }

  return null;
};
